/**
 * AuthComplete - Handles magic link sign-in completion
 * 
 * This page is shown when user clicks the magic link in their email.
 * It completes the sign-in, migrates any anonymous account data, and redirects to the main app.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle, AlertCircle, Mail } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { 
  isEmailSignInLink, 
  completeSignInWithEmailLink,
  getStoredEmailForSignIn,
  getAnonymousUidForLinking,
  clearAnonymousUidForLinking,
} from '../services/authService';

type AuthState = 'checking' | 'needEmail' | 'completing' | 'success' | 'error';

export const AuthComplete: React.FC = () => {
  const navigate = useNavigate();
  const [authState, setAuthState] = useState<AuthState>('checking');
  const [email, setEmail] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [giftRedeemed, setGiftRedeemed] = useState<{ amount: number } | null>(null);

  useEffect(() => {
    const handleSignIn = async () => {
      // Check if this is a valid sign-in link
      if (!isEmailSignInLink()) {
        console.log('Not a valid sign-in link, redirecting to home');
        navigate('/');
        return;
      }

      // Try to get the email from localStorage
      const storedEmail = getStoredEmailForSignIn();
      
      if (storedEmail) {
        // We have the email - complete sign-in automatically
        setAuthState('completing');
        try {
          const user = await completeSignInWithEmailLink(storedEmail);
          setUserEmail(user.email);
          
          const functions = getFunctions();
          
          // Check if we need to link an anonymous account
          const anonymousUid = getAnonymousUidForLinking();
          if (anonymousUid && anonymousUid !== user.uid) {
            console.log('🔗 Linking anonymous account:', anonymousUid);
            try {
              const linkAccount = httpsCallable(functions, 'linkAnonymousAccount');
              const result = await linkAccount({ anonymousUid });
              console.log('🔗 Account linking result:', result.data);
            } catch (linkError) {
              // Log but don't fail sign-in if linking fails
              console.error('🔗 Account linking error (non-fatal):', linkError);
            }
            clearAnonymousUidForLinking();
          }
          
          // Check and redeem any gift for this email
          try {
            const redeemGift = httpsCallable(functions, 'redeemGiftIfExists');
            const giftResult = await redeemGift({});
            const giftData = giftResult.data as { redeemed: boolean; amount?: number };
            if (giftData.redeemed && giftData.amount) {
              console.log('🎁 Gift redeemed:', giftData.amount);
              setGiftRedeemed({ amount: giftData.amount });
            }
          } catch (giftError) {
            // Log but don't fail sign-in if gift redemption fails
            console.error('🎁 Gift redemption error (non-fatal):', giftError);
          }
          
          setAuthState('success');
          
          // Redirect to home after a brief delay
          // Include ?magiclink=success so ChatLayout can send any pending prompt
          setTimeout(() => {
            navigate('/?magiclink=success');
          }, 2500);
        } catch (error: any) {
          console.error('Sign-in error:', error);
          setErrorMessage(error.message || 'Failed to complete sign-in.');
          setAuthState('error');
        }
      } else {
        // No stored email - user needs to enter it
        // This happens when they click the link on a different device
        setAuthState('needEmail');
      }
    };

    handleSignIn();
  }, [navigate]);

  const handleSubmitEmail = async () => {
    if (!email.trim()) {
      setErrorMessage('Please enter your email address.');
      return;
    }

    setAuthState('completing');
    setErrorMessage('');

    try {
      const user = await completeSignInWithEmailLink(email.trim());
      setUserEmail(user.email);
      
      const functions = getFunctions();
      
      // Check if we need to link an anonymous account
      const anonymousUid = getAnonymousUidForLinking();
      if (anonymousUid && anonymousUid !== user.uid) {
        console.log('🔗 Linking anonymous account:', anonymousUid);
        try {
          const linkAccount = httpsCallable(functions, 'linkAnonymousAccount');
          const result = await linkAccount({ anonymousUid });
          console.log('🔗 Account linking result:', result.data);
        } catch (linkError) {
          // Log but don't fail sign-in if linking fails
          console.error('🔗 Account linking error (non-fatal):', linkError);
        }
        clearAnonymousUidForLinking();
      }
      
      // Check and redeem any gift for this email
      try {
        const redeemGift = httpsCallable(functions, 'redeemGiftIfExists');
        const giftResult = await redeemGift({});
        const giftData = giftResult.data as { redeemed: boolean; amount?: number };
        if (giftData.redeemed && giftData.amount) {
          console.log('🎁 Gift redeemed:', giftData.amount);
          setGiftRedeemed({ amount: giftData.amount });
        }
      } catch (giftError) {
        // Log but don't fail sign-in if gift redemption fails
        console.error('🎁 Gift redemption error (non-fatal):', giftError);
      }
      
      setAuthState('success');
      
      // Redirect to home after a brief delay
      // Include ?magiclink=success so ChatLayout can send any pending prompt
      setTimeout(() => {
        navigate('/?magiclink=success');
      }, 2500);
    } catch (error: any) {
      console.error('Sign-in error:', error);
      setErrorMessage(error.message || 'Failed to complete sign-in.');
      setAuthState('error');
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-gray-900 rounded-2xl shadow-2xl p-8 border border-gray-800">
        
        {/* Checking State */}
        {authState === 'checking' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-100 mb-2">
              Verifying sign-in link...
            </h2>
            <p className="text-gray-400 text-sm">
              Please wait a moment.
            </p>
          </div>
        )}

        {/* Need Email State */}
        {authState === 'needEmail' && (
          <div>
            <div className="text-center mb-6">
              <Mail className="w-12 h-12 mx-auto mb-4 text-blue-400" />
              <h2 className="text-xl font-semibold text-gray-100 mb-2">
                Confirm your email
              </h2>
              <p className="text-gray-400 text-sm">
                To complete sign-in, please enter the email address you used to request this link.
              </p>
            </div>

            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmitEmail()}
                placeholder="you@example.com"
                className="w-full px-4 py-3 rounded-lg bg-gray-800 border border-gray-700 text-gray-100 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                autoFocus
              />
            </div>

            {errorMessage && (
              <div className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20">
                <p className="text-sm text-red-400">{errorMessage}</p>
              </div>
            )}

            <button
              onClick={handleSubmitEmail}
              className="w-full py-3 px-4 rounded-lg font-medium bg-blue-600 hover:bg-blue-500 text-white transition-colors"
            >
              Complete Sign-In
            </button>
          </div>
        )}

        {/* Completing State */}
        {authState === 'completing' && (
          <div className="text-center">
            <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-blue-400" />
            <h2 className="text-xl font-semibold text-gray-100 mb-2">
              Signing you in...
            </h2>
            <p className="text-gray-400 text-sm">
              Please wait a moment.
            </p>
          </div>
        )}

        {/* Success State */}
        {authState === 'success' && (
          <div className="text-center">
            <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-400" />
            <h2 className="text-xl font-semibold text-gray-100 mb-2">
              {giftRedeemed ? 'Welcome to ionoff!' : 'Welcome back!'}
            </h2>
            {userEmail && (
              <p className="text-gray-400 text-sm mb-2">
                Signed in as <span className="text-gray-200">{userEmail}</span>
              </p>
            )}
            {giftRedeemed && (
              <p className="text-green-400 text-sm mb-4">
                🎁 Gift of ${giftRedeemed.amount.toFixed(2)} credited to your account!
              </p>
            )}
            <p className="text-gray-500 text-sm">
              Redirecting to ionoff...
            </p>
          </div>
        )}

        {/* Error State */}
        {authState === 'error' && (
          <div className="text-center">
            <AlertCircle className="w-12 h-12 mx-auto mb-4 text-red-400" />
            <h2 className="text-xl font-semibold text-gray-100 mb-2">
              Sign-in failed
            </h2>
            <p className="text-gray-400 text-sm mb-6">
              {errorMessage}
            </p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/')}
                className="w-full py-3 px-4 rounded-lg font-medium bg-gray-800 hover:bg-gray-700 text-gray-300 border border-gray-700 transition-colors"
              >
                Go to Home
              </button>
              <p className="text-gray-500 text-xs">
                Try requesting a new sign-in link from the home page.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
